/**
 * The standing detectors, on a screen.
 *
 * WHY THIS SCREEN EXISTS AT ALL: every detector in the system already raises
 * through `sendOpsAlert`, and that path is only as good as the email
 * configuration behind it. A detector nobody receives is not a detector. This
 * page reads the same composite report the monitor reads, so an operator can
 * always answer "is anything wrong right now" without depending on delivery.
 *
 * THREE RULES THE LAYOUT ENFORCES, each of which is a way a status page lies:
 *
 * 1. `notCovered` IS RENDERED, ALWAYS, AND NEVER COLLAPSED AWAY. A green page
 *    that silently omits what it could not evaluate reads as broader coverage
 *    than it has, and that is the single most expensive thing a status page can
 *    do. If the server says it did not look at something, the operator sees it.
 *
 * 2. `detail` IS RENDERED AS JSON, not as fields. Its shape varies by detector
 *    and the contract says so explicitly. Picking keys out of it would compile
 *    today and quietly render blanks the first time a detector adds a scope.
 *
 * 3. The alert list is NOT sorted or filtered client-side. The server returns
 *    every detector on every call — that completeness is the invariant — and a
 *    "hide the OK ones" toggle turns a set you can count into a set you cannot.
 *    Critical rows carry a ring instead, so severity is found by looking rather
 *    than by filtering.
 *
 * Auto-refresh is OPT-IN and off by default. A console left open on a spare
 * monitor polling forever is a load pattern nobody chose.
 */
import { useState } from 'react';
import { useOpsAlerts } from '@/hooks/useAdmin';
import { StatusPill } from '@/components/ui/StatusPill';
import { Card } from '@/components/ui/Card';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import type { OpsAlert } from '@/types/api';

const REFRESH_MS = 30_000;

/** Detector ids are stable; these titles make them readable without decoding. */
const TITLES: Record<string, string> = {
  delayed_price_exposure_drift: 'Delayed-price exposure drift',
  abandoned_market_exposure: 'Bets on abandoned markets',
  ledger_conservation: 'Ledger conservation',
  ledger_reconciliation: 'Ledger reconciliation',
  stuck_deposits: 'Stuck deposits',
  orphan_deposits: 'Orphan deposits',
  scheduler_liveness: 'Scheduler liveness',
};

function timeAgo(iso: string, now: number): string {
  const secs = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function AlertCard({ alert }: { alert: OpsAlert }) {
  const [open, setOpen] = useState(false);
  const hasDetail = Object.keys(alert.detail ?? {}).length > 0;

  return (
    <div
      className={`rounded-2xl border p-4 transition ${
        alert.status === 'critical'
          ? 'border-red-400/35 bg-red-500/[0.06]'
          : alert.status === 'warn'
            ? 'border-brand-gold/25 bg-brand-gold/[0.05]'
            : 'border-white/[0.08] bg-brand-surface/60'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-white/90">{TITLES[alert.id] ?? alert.id}</h3>
        <StatusPill status={alert.status} />
      </div>

      <p className="mt-2 text-sm leading-relaxed text-white/60">{alert.summary}</p>

      {/* `action` is '' when the detector is ok — an empty box of advice reads
          as missing guidance rather than as nothing to do. */}
      {alert.action !== '' && (
        <p className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm leading-relaxed text-white/75">
          <span className="mr-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white/35">Do</span>
          {alert.action}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <code className="font-mono text-[11px] text-white/25">{alert.id}</code>
        {hasDetail && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg border border-white/10 px-2.5 py-1 text-[11px] font-medium text-white/50 transition hover:bg-white/[0.06] hover:text-white/80"
          >
            {open ? 'Hide evidence' : 'Show evidence'}
          </button>
        )}
      </div>

      {open && hasDetail && (
        <pre className="mt-3 max-h-72 overflow-auto rounded-lg border border-white/[0.07] bg-black/30 p-3 font-mono text-[11px] leading-relaxed text-white/60">
          {JSON.stringify(alert.detail, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function OpsAlertsPage() {
  const [live, setLive] = useState(false);
  const query = useOpsAlerts(live ? { refetchInterval: REFRESH_MS } : {});
  const { data, isLoading, isError, error, refetch, isFetching } = query;

  if (isLoading) return <AdminLoading label="Running detectors" />;
  if (isError || !data) return <AdminError error={error} onRetry={() => void refetch()} />;

  /* Age is relative to the fetch, not to render — see MarketVisibilityPage. */
  const now = query.dataUpdatedAt;

  return (
    <>
      <AdminPageHeader
        title="Ops alerts"
        subtitle="Every standing detector, evaluated now. A detector that could not be evaluated reports critical — “couldn’t check” is never “nothing wrong”."
        actions={
          <>
            <button
              onClick={() => setLive((v) => !v)}
              className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                live
                  ? 'border-brand-teal/30 bg-brand-teal/10 text-brand-teal'
                  : 'border-white/15 bg-white/[0.05] text-white/70 hover:bg-white/10'
              }`}
            >
              {live ? 'Auto-refresh on' : 'Auto-refresh off'}
            </button>
            <button
              onClick={() => void refetch()}
              disabled={isFetching}
              className="rounded-xl border border-white/15 bg-white/[0.05] px-3 py-2 text-sm font-medium text-white/80 transition hover:bg-white/10 disabled:opacity-40"
            >
              {isFetching ? 'Checking…' : 'Re-check'}
            </button>
          </>
        }
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <StatusPill status={data.overall} className="text-[11px]" />
            <div>
              <p className="text-sm font-semibold">
                {data.overall === 'ok'
                  ? 'All detectors clear'
                  : data.overall === 'warn'
                    ? 'Something needs a look'
                    : 'Something needs attention now'}
              </p>
              <p className="mt-0.5 text-xs text-white/40">
                Checked {timeAgo(data.checkedAt, now)} ·{' '}
                {new Date(data.checkedAt).toLocaleTimeString('en-AU')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 tabular-nums">
            {(['critical', 'warn', 'ok'] as const).map((k) => (
              <div key={k} className="text-center">
                <div
                  className={`text-lg font-bold ${
                    k === 'critical'
                      ? 'text-red-300'
                      : k === 'warn'
                        ? 'text-brand-gold'
                        : 'text-brand-green'
                  }`}
                >
                  {data.counts[k]}
                </div>
                <div className="text-[10px] uppercase tracking-[0.08em] text-white/30">{k}</div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        {data.alerts.map((alert) => (
          <AlertCard key={alert.id} alert={alert} />
        ))}
      </div>

      {/* Always rendered, even when empty — see rule 1 in the header. */}
      <div className="mt-6">
        <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-white/35">
          Not covered by this report
        </h2>
        {data.notCovered.length === 0 ? (
          <p className="text-sm text-white/35">
            Nothing — every detector in the set was evaluated on this run.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.notCovered.map((n) => (
              <li
                key={n.id}
                className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2.5 text-sm"
              >
                <code className="font-mono text-[11px] text-white/40">{n.id}</code>
                <p className="mt-1 leading-relaxed text-white/55">{n.reason}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
